import { supportedLanguages, type SupportedLanguage } from '../locales';

/**
 * English is served un-prefixed at the site root (`/rooms`, `/guides/:slug`) so the
 * URLs already indexed by search engines keep working. Every other language lives
 * under its own prefix (`/de/rooms`), which is what makes hreflang valid: each
 * language needs a distinct, crawlable URL of its own.
 */
export const DEFAULT_LANGUAGE: SupportedLanguage = 'en';

/** Languages that carry a URL prefix (i.e. everything except the default). */
export const PREFIXED_LANGUAGES = supportedLanguages.filter(
  (lang) => lang !== DEFAULT_LANGUAGE,
);

export const RTL_LANGUAGES: readonly string[] = ['ar'];

/**
 * Open Graph expects full locale codes. Keep in sync with OG_LOCALES in
 * scripts/prerender.mjs, which emits the same tags into the static HTML.
 */
export const OG_LOCALES: Record<SupportedLanguage, string> = {
  en: 'en_US',
  fr: 'fr_FR',
  es: 'es_ES',
  de: 'de_DE',
  it: 'it_IT',
  nl: 'nl_NL',
  pt: 'pt_PT',
  ar: 'ar_MA',
};

export function isSupportedLanguage(value: string | undefined): value is SupportedLanguage {
  return !!value && (supportedLanguages as readonly string[]).includes(value);
}

/** Normalises `de-DE` / `DE` to a supported code, or returns the default. */
export function normalizeLanguage(value: string | undefined | null): SupportedLanguage {
  if (!value) return DEFAULT_LANGUAGE;
  const lower = value.toLowerCase();
  if (isSupportedLanguage(lower)) return lower;
  const base = lower.split('-')[0];
  return isSupportedLanguage(base) ? base : DEFAULT_LANGUAGE;
}

/**
 * Splits a pathname into its language prefix and the language-agnostic route.
 * `/de/guides/x` -> `{ lang: 'de', path: '/guides/x' }`
 * `/guides/x`    -> `{ lang: 'en', path: '/guides/x' }`
 * `/de`          -> `{ lang: 'de', path: '/' }`
 */
export function splitLanguageFromPath(pathname: string): {
  lang: SupportedLanguage;
  path: string;
} {
  const segments = pathname.split('/').filter(Boolean);
  const first = segments[0]?.toLowerCase();

  if (isSupportedLanguage(first) && first !== DEFAULT_LANGUAGE) {
    const rest = segments.slice(1).join('/');
    return { lang: first, path: rest ? `/${rest}` : '/' };
  }

  return { lang: DEFAULT_LANGUAGE, path: pathname || '/' };
}

/**
 * Builds the URL for `path` in `lang`. `path` may already carry a prefix — it is
 * stripped first, so this is safe to call with the current location.
 */
export function localizePath(path: string, lang: string): string {
  const language = normalizeLanguage(lang);
  const { path: bare } = splitLanguageFromPath(path);
  const clean = bare.startsWith('/') ? bare : `/${bare}`;

  if (language === DEFAULT_LANGUAGE) return clean;
  return clean === '/' ? `/${language}` : `/${language}${clean}`;
}
